import cloneDeepWith from 'lodash/cloneDeepWith';
import { getCurrencyConfig, WorldReadyProps } from '@client/utils/localeHelper';
import { KrakenRequest } from '@server/typings';
import { getPercentAmount } from '@client/utils/utils';
import { DiscountType } from '@client/components/buttons/types';

// To support both the server and client side typings for WorldReady.
type WorldReadyFlex = WorldReadyProps | KrakenRequest['worldReady'];
/**
 * Recursively trims all string values within a given input object or array.
 *
 * Uses lodash's `cloneDeepWith` to create a deep clone of the input,
 * trimming whitespace from all string values it encounters.
 * Non-string values are left untouched.
 *
 * @param {any} input - The input value (object, array, or primitive) to deep-trim.
 * @returns {any} A deep-cloned version of the input with all strings trimmed.
 *
 * @example
 * const input = { name: '  Alice ', nested: { desc: ' Hello ' }, list: ['  a ', ' b'] };
 * const result = deepTrim(input);
 * // result => { name: 'Alice', nested: { desc: 'Hello' }, list: ['a', 'b'] }
 */
export const deepTrim = <T>(input: T): T => {
  return cloneDeepWith(input, (value) => {
    if (typeof value === 'string') {
      return value.trim();
    }
    return undefined;
  });
};

export const isNumPositive = (value?: string | number) => {
  const valueNum = Number(value);
  return value && !Number.isNaN(valueNum) && Number(valueNum) > 0;
};

/**
 * Calculates the discount amount based on either a fixed discount amount or a discount rate.
 *
 * @param amount The original amount (price) of the item. Can be a string or number. If not a valid positive number, returns 0.
 * @param discountAmount The fixed amount to discount. Can be a string or number. If both `discountAmount` and `discountRate` are provided and the discount type is not specified, `discountAmount` takes precedence.
 * @param discountRate The percentage rate to discount (e.g., 10 for 10%). Can be a string or number.
 * @param discountType Specifies the type of discount to apply, either 'rate' for percentage-based discounts or 'flat' for fixed amount discounts.
 * @param maxDecimals The maximum number of decimal places to round the discount to. Defaults to 2.
 * @param returnPositive A boolean indicating whether the discount should be returned as a positive number. Defaults to true. If false, the discount is returned as a negative number.
 * @returns The calculated discount amount as a number. Returns 0 if no valid amount or discount information is provided.
 */
export const getDiscount = (
  amount?: string | number,
  discountAmount?: string | number,
  discountRate?: string | number,
  discountType?: string,
  maxDecimals: number = 2,
  returnPositive: boolean = true
): number => {
  const amountNum = Number(amount);
  const discountRateNum = Number(discountRate);
  const discountAmountNum = Number(discountAmount);
  let discount = 0;

  if (Number.isNaN(amountNum) || Number(amountNum) <= 0) {
    return discount;
  }

  // payment page will not have discount_type passed in but preview page will
  if (isNumPositive(discountRate) && (!discountType || discountType === DiscountType.Rate)) {
    discount = getPercentAmount(amountNum, discountRateNum, maxDecimals);
  } else if (
    isNumPositive(discountAmount) &&
    (!discountType || discountType === DiscountType.Flat)
  ) {
    discount = discountAmountNum;
  }

  if (discount === 0) {
    return discount;
  }

  return returnPositive ? discount : discount * -1;
};

export const getDiscountedPrice = (
  worldReady: WorldReadyFlex,
  currency?: string,
  amount?: string | number,
  discountAmount?: string | number,
  discountRate?: string | number
) => {
  const amountNum = Number(amount);
  if (Number.isNaN(amountNum)) {
    return '0';
  }

  const currencyConfig = getCurrencyConfig(worldReady, currency);
  const { maxDecimalPlaces } = currencyConfig;
  const discountRateNum = Number(discountRate);
  const discountAmountNum = Number(discountAmount);
  if (discountRate && !Number.isNaN(discountRateNum) && discountRateNum > 0) {
    return amountNum - getPercentAmount(amountNum, discountRateNum, maxDecimalPlaces);
  }
  if (discountAmount && !Number.isNaN(discountAmountNum) && discountAmountNum > 0) {
    return amountNum - discountAmountNum;
  }
  return amountNum;
};

export const hasDiscount = (
  discountAmount?: string | number,
  discountRate?: string | number
): boolean => {
  return Boolean(
    (discountAmount && Number(discountAmount) > 0) || (discountRate && Number(discountRate) > 0)
  );
};
